import { Suspense, lazy, memo, useEffect, useMemo, useState } from "react";
import { BadgeCheck } from "lucide-react";
import { withStaleChunkRecovery } from "@/lib/lazy-with-reload";

interface SpecIconProps {
  /**
   * Kebab-case lucide icon name (e.g. "circle-dot", "ruler", "anchor").
   * See https://lucide.dev/icons for the full list — the CMS field
   * accepts any name from there. Empty/unknown names render the
   * BadgeCheck fallback so the FE never crashes on a typo.
   */
  name?: string;
  className?: string;
  size?: number | string;
}

type IconLoader = () => Promise<{
  default: React.ComponentType<{ className?: string; size?: number | string }>;
}>;
type IconCatalog = Record<string, IconLoader>;

/**
 * lucide's `dynamicIconImports` is a static map of ~1700 lazy
 * loader functions keyed by kebab-case icon name. Just IMPORTING
 * that map costs ~100 KB raw / 25 KB gz in the consuming chunk
 * even though individual icons load on demand from there.
 *
 * Defer the catalog itself — most pages either don't render any
 * SpecIcon at all OR the icons are below-the-fold (spec tables,
 * product cards). The component renders the BadgeCheck fallback
 * until the catalog promise resolves, after which `resolveIcon`
 * has the loader-by-name map and the per-icon lazy() chunks fire
 * the same way they did before.
 */
let catalogPromise: Promise<IconCatalog> | null = null;
const loadCatalogChunk = withStaleChunkRecovery(() =>
  import("lucide-react/dynamicIconImports"),
);
function loadIconCatalog(): Promise<IconCatalog> {
  if (!catalogPromise) {
    catalogPromise = loadCatalogChunk().then(
      (mod) => mod.default as unknown as IconCatalog,
    );
  }
  return catalogPromise;
}

/**
 * Per-icon-name component cache. lazy() returns a stable component
 * for a given factory; we want a stable component PER name so the
 * Suspense boundary doesn't refetch on every render. The cache key
 * is the kebab-case name.
 */
const iconCache = new Map<string, React.LazyExoticComponent<React.ComponentType<{
  className?: string;
  size?: number | string;
}>>>();

/**
 * Renders a lucide icon by kebab-case name. Falls back to BadgeCheck
 * for unknown names so a typo never breaks the page; renders nothing
 * during the lazy chunk fetch (icons are tiny — typically renders in
 * < 50ms after navigation, invisible to the user).
 */
const SpecIconImpl = ({ name, className, size = 16 }: SpecIconProps) => {
  // SSR-safe gate. The lazy(loader) wrapper for each lucide icon
  // suspends on first read, and renderToString can't handle that —
  // it emits "The server did not finish this Suspense boundary"
  // template markers into the SSR HTML, which then fail to hydrate
  // (React #418). Holding the Icon back behind hasMounted means SSR
  // and the first client render both emit the BadgeCheck fallback,
  // then the lazy icon mounts after hydration commits — clean.
  const [hasMounted, setHasMounted] = useState(false);
  // Catalog state — null until the lazy `loadIconCatalog()` import
  // resolves. We don't kick that off until after hydration so the
  // 100 KB lucide catalog stays out of the first-paint critical path.
  const [catalog, setCatalog] = useState<IconCatalog | null>(null);
  useEffect(() => {
    setHasMounted(true);
    let cancelled = false;
    loadIconCatalog()
      .then((cat) => {
        if (!cancelled) setCatalog(cat);
      })
      .catch(() => {
        // Catalog failed to load (rare — chunk-network failure on
        // a stale-deploy session). Leave catalog null so the
        // BadgeCheck fallback keeps rendering. No regression vs.
        // the unresolvable-name path that already used the same
        // fallback.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // useMemo so the resolved component is stable across re-renders
  // (otherwise we'd re-create the lazy() wrapper each render and
  // Suspense would re-suspend on every parent state change).
  const Icon = useMemo(() => {
    if (!name || !catalog) return null;
    const cached = iconCache.get(name);
    if (cached) return cached;
    const loader = catalog[name];
    if (!loader) return null;
    // Wrap the per-icon loader in stale-chunk recovery so a failed
    // icon-chunk fetch (cached HTML pointing at an old deploy's
    // hash) triggers the same one-shot reload guard the route
    // chunks already use. Without this the failure surfaces in
    // ErrorBoundary instead of self-recovering.
    const Component = lazy(withStaleChunkRecovery(loader));
    iconCache.set(name, Component);
    return Component;
  }, [name, catalog]);

  if (!Icon || !hasMounted) {
    return <BadgeCheck className={className} size={size} aria-hidden="true" />;
  }
  return (
    <Suspense fallback={<BadgeCheck className={className} size={size} aria-hidden="true" />}>
      <Icon className={className} size={size} aria-hidden="true" />
    </Suspense>
  );
};

const SpecIcon = memo(SpecIconImpl);
export default SpecIcon;
